import { useCallback } from 'react';

import { useLocalStorage } from './useLocalStorage';

const STORAGE_KEY = 'calmcycle:settings-v1';

export interface CycleSettings {
  /** ISO date the last period started (manual prediction override). */
  lastPeriodStart?: string;
  /** Manual cycle length in days (leave unset to auto-detect from logs). */
  cycleLength?: number;
  /** Symptoms the user added beyond the built-in list. */
  customSymptoms: string[];
  /**
   * Whether the user experiences painful flare-ups. When false the app uses
   * "gentle mode": predictions focus on period timing, not pain.
   */
  trackFlareUps: boolean;
  /** Show the flow selector in the daily log form. */
  trackFlow: boolean;
  /** Show the pain severity slider in the daily log form. */
  trackPain: boolean;
  /** Show the heart rate / HRV sync section in the daily log form. */
  trackVitals: boolean;
}

const DEFAULT_SETTINGS: CycleSettings = {
  customSymptoms: [],
  trackFlareUps: true,
  trackFlow: true,
  trackPain: true,
  trackVitals: true,
};

export function useCycleSettings() {
  const [raw, setSettings] = useLocalStorage<CycleSettings>(STORAGE_KEY, DEFAULT_SETTINGS);

  // Merge with defaults so stored settings from an older app version (or a
  // partially-written value) can never be missing fields.
  const settings: CycleSettings = {
    ...DEFAULT_SETTINGS,
    ...raw,
    customSymptoms: Array.isArray(raw?.customSymptoms) ? raw.customSymptoms : [],
    trackFlareUps: typeof raw?.trackFlareUps === 'boolean' ? raw.trackFlareUps : true,
    trackFlow: typeof raw?.trackFlow === 'boolean' ? raw.trackFlow : true,
    trackPain: typeof raw?.trackPain === 'boolean' ? raw.trackPain : true,
    trackVitals: typeof raw?.trackVitals === 'boolean' ? raw.trackVitals : true,
  };

  const update = useCallback(
    (patch: Partial<CycleSettings>) => {
      setSettings((prev) => ({ ...DEFAULT_SETTINGS, ...prev, ...patch }));
    },
    [setSettings],
  );

  const addSymptom = useCallback(
    (name: string) => {
      const trimmed = name.trim();
      if (trimmed.length < 2) return;
      setSettings((prev) => {
        const exists = prev.customSymptoms.some(
          (s) => s.toLowerCase() === trimmed.toLowerCase(),
        );
        return exists ? prev : { ...prev, customSymptoms: [...prev.customSymptoms, trimmed] };
      });
    },
    [setSettings],
  );

  const removeSymptom = useCallback(
    (name: string) => {
      setSettings((prev) => ({
        ...prev,
        customSymptoms: prev.customSymptoms.filter((s) => s !== name),
      }));
    },
    [setSettings],
  );

  return { settings, update, addSymptom, removeSymptom };
}
